import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress,
  TextField,
  MenuItem,
  Grid,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Switch,
  Divider,
  Card,
  CardContent,
  Avatar,
  Fade,
  Collapse
} from '@mui/material';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { 
  Save as SaveIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import AlertMessage from '../components/common/AlertMessage';
import { TableService } from '../services/table.service';
import { TableDataService } from '../services/tableData.service';
import { DataTable, TableColumn, ColumnType } from '../types/table.types';
import { TableData } from '../types/tableData.types';

type FormMode = 'create' | 'edit';

export default function TableDataForm() {
  const { id, dataId } = useParams<{ id: string; dataId: string }>();
  const navigate = useNavigate();
  const mode: FormMode = dataId ? 'edit' : 'create';
  
  const [table, setTable] = useState<DataTable | null>(null);
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  useEffect(() => {
    if (id) {
      fetchTable(id);
    }
  }, [id]);
  
  useEffect(() => {
    if (mode === 'edit' && id && dataId) {
      fetchTableData(id, dataId);
    }
  }, [mode, id, dataId]);
  
  const fetchTable = async (tableId: string) => {
    try {
      setLoading(true);
      const tableData = await TableService.getTableById(tableId);
      setTable(tableData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки таблицы');
    } finally {
      if (mode === 'create') {
        setLoading(false);
      }
    }
  };
  
  const fetchTableData = async (tableId: string, dataId: string) => {
    try {
      const data = await TableDataService.getTableDataById(tableId, dataId);
      setTableData(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };
  
  const createValidationSchema = () => {
    if (!table) return yup.object().shape({});
    
    const schemaFields: Record<string, any> = {};
    
    table.columns.forEach(column => {
      let field;
      
      switch (column.type) {
        case ColumnType.NUMBER:
          field = yup.number().typeError('Должно быть числом');
          break;
        case ColumnType.DATE:
          field = yup.date().typeError('Должна быть дата');
          break;
        case ColumnType.BOOLEAN:
          field = yup.boolean();
          break;
        case ColumnType.DROPDOWN:
          field = yup.string();
          break;
        default:
          field = yup.string();
      }
      
      if (column.isRequired) {
        field = field.required('Обязательное поле');
      }
      
      schemaFields[column.name] = field;
    });
    
    return yup.object().shape(schemaFields);
  };
  
  const schema = createValidationSchema();
  
  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: tableData?.data || {},
    values: tableData?.data
  });
  
  const onSubmit: SubmitHandler<Record<string, any>> = async (data) => {
    if (!id) return;
    
    try {
      setSubmitting(true);
      
      if (mode === 'create') {
        await TableDataService.createTableData(id, data);
        setSuccess('Запись успешно создана');
      } else if (mode === 'edit' && dataId) {
        await TableDataService.updateTableData(id, dataId, data);
        setSuccess('Запись успешно обновлена');
      }
      
      setTimeout(() => {
        navigate(`/tables/${id}`);
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка сохранения данных');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    navigate(`/tables/${id}`);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!table) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="error">
          Таблица не найдена или у вас нет доступа к ней
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/tables')}
          sx={{ mt: 2 }}
        >
          Вернуться к списку таблиц
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <PageHeader 
        title={mode === 'create' ? 'Создание записи' : 'Редактирование записи'}
        subtitle={`Таблица: ${table.name}`}
        breadcrumbs={[
          { title: 'Таблицы', href: '/tables' },
          { title: table.name, href: `/tables/${id}` },
          { title: mode === 'create' ? 'Создание' : 'Редактирование' },
        ]}
      />

      <Collapse in={!!error}>
        <Fade in={!!error}>
          <Box sx={{ mb: 3 }}>
            <AlertMessage
              open={!!error}
              message={error || ''}
              severity="error"
              onClose={() => setError(null)}
            />
          </Box>
        </Fade>
      </Collapse>

      <Collapse in={!!success}>
        <Fade in={!!success}>
          <Box sx={{ mb: 3 }}>
            <AlertMessage
              open={!!success}
              message={success || ''}
              severity="success"
              onClose={() => setSuccess(null)}
            />
          </Box>
        </Fade>
      </Collapse>
      
      <Fade in timeout={600}>
        <Paper 
          sx={{ 
            p: 4,
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: mode === 'create' 
                ? 'linear-gradient(90deg, #4caf50 0%, #8bc34a 100%)'
                : 'linear-gradient(90deg, #2196f3 0%, #03a9f4 100%)',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar 
              sx={{ 
                bgcolor: mode === 'create' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(33, 150, 243, 0.1)',
                color: mode === 'create' ? '#4caf50' : '#2196f3',
                width: 56, 
                height: 56 
              }}
            >
              {mode === 'create' ? <AddIcon sx={{ fontSize: 30 }} /> : <EditIcon sx={{ fontSize: 30 }} />}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {mode === 'create' ? 'Новая запись' : 'Редактирование записи'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Заполните все обязательные поля
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />
          
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {table.columns.map((column) => (
                <Grid size={{ xs: 12, sm: 6 }} key={column.id}>
                  {renderFormField(column, control, errors)}
                </Grid>
              ))}
            </Grid>
            
            <Divider sx={{ my: 4 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleCancel}
                disabled={submitting}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Отмена
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  background: mode === 'create'
                    ? 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)'
                    : 'linear-gradient(135deg, #2196f3 0%, #03a9f4 100%)',
                  '&:hover': {
                    background: mode === 'create'
                      ? 'linear-gradient(135deg, #8bc34a 0%, #4caf50 100%)'
                      : 'linear-gradient(135deg, #03a9f4 0%, #2196f3 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(0,0,0,0.12)',
                  }
                }}
              >
                {submitting ? 'Сохранение...' : mode === 'create' ? 'Создать' : 'Сохранить'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Fade>
    </Box>
  );
}

function renderFormField(column: TableColumn, control: any, errors: any) {
  switch (column.type) {
    case ColumnType.TEXT:
      return (
        <Controller
          name={column.name}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={column.name}
              fullWidth
              required={column.isRequired}
              error={!!errors[column.name]}
              helperText={errors[column.name]?.message}
              multiline={Boolean(column.options?.multiline)}
              rows={column.options?.multiline ? 4 : 1}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          )}
        />
      );
    
    case ColumnType.NUMBER:
      return (
        <Controller
          name={column.name}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={column.name}
              fullWidth
              type="number"
              required={column.isRequired}
              error={!!errors[column.name]}
              helperText={errors[column.name]?.message}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          )}
        />
      );
    
    case ColumnType.DATE:
      return (
        <Controller
          name={column.name}
          control={control}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={column.name}
                value={field.value ? dayjs(field.value) : null}
                onChange={(date) => field.onChange(date ? date.toISOString() : null)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: column.isRequired,
                    error: !!errors[column.name],
                    helperText: errors[column.name]?.message,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }
                  },
                }}
              />
            </LocalizationProvider>
          )}
        />
      );
    
    case ColumnType.BOOLEAN:
      return (
        <Controller
          name={column.name}
          control={control}
          render={({ field }) => (
            <Card
              sx={{
                bgcolor: 'rgba(0,0,0,0.02)',
                boxShadow: 'none',
                border: '1px solid rgba(0,0,0,0.08)',
              }}
            >
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(field.value)}
                      onChange={(e) => field.onChange(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#667eea',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#667eea',
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {column.name}
                      </Typography>
                      {column.isRequired && (
                        <Typography variant="caption" color="error">
                          Обязательное поле
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </CardContent>
            </Card>
          )}
        />
      );
    
    case ColumnType.DROPDOWN:
      return (
        <Controller
          name={column.name}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label={column.name}
              fullWidth
              required={column.isRequired}
              error={!!errors[column.name]}
              helperText={errors[column.name]?.message}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            >
              {column.options?.values?.map((option: string) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      );
    
    default:
      return null;
  }
}